/**
 * useUIState - Consolidated UI panel state management
 *
 * Manages all collapse/expand states for GeoTiffViewer panels and controls.
 * Reduces component complexity by grouping related UI states.
 */

import { useState, useEffect, useCallback } from 'react';

export interface UIState {
  // Card collapse states
  roofCardCollapsed: boolean;
  obstructionCardCollapsed: boolean;
  moduleCardCollapsed: boolean;
  fieldSegmentsCollapsed: boolean;
  menuCollapsed: boolean;
  leftPanelCollapsed: boolean;

  // Dropdown/accordion open states
  controlsOpen: boolean;
  mapModeOpen: boolean;
  simplificationOpen: boolean;
  polygonToggleOpen: boolean;
  obstructionsOpen: boolean;
  snapEdgeOpen: boolean;
  moduleAdvancedOpen: boolean;
}

export interface UIStateActions {
  // Card toggles
  setRoofCardCollapsed: (value: boolean) => void;
  setObstructionCardCollapsed: (value: boolean) => void;
  setModuleCardCollapsed: (value: boolean) => void;
  setFieldSegmentsCollapsed: (value: boolean) => void;
  setMenuCollapsed: (value: boolean) => void;
  setLeftPanelCollapsed: (value: boolean) => void;

  // Dropdown toggles
  setControlsOpen: (value: boolean) => void;
  setMapModeOpen: (value: boolean) => void;
  setSimplificationOpen: (value: boolean) => void;
  setPolygonToggleOpen: (value: boolean) => void;
  setObstructionsOpen: (value: boolean) => void;
  setSnapEdgeOpen: (value: boolean) => void;
  setModuleAdvancedOpen: (value: boolean) => void;

  // Convenience methods
  toggleRoofCard: () => void;
  toggleObstructionCard: () => void;
  toggleModuleCard: () => void;
  toggleFieldSegments: () => void;
  toggleMenu: () => void;
  toggleLeftPanel: () => void;
  closeAllDropdowns: () => void;
}

export interface UseUIStateOptions {
  compactMode?: boolean;
}

export function useUIState(options: UseUIStateOptions = {}): UIState & UIStateActions {
  const { compactMode = false } = options;

  // Card collapse states
  const [roofCardCollapsed, setRoofCardCollapsed] = useState(true);
  const [obstructionCardCollapsed, setObstructionCardCollapsed] = useState(true);
  const [moduleCardCollapsed, setModuleCardCollapsed] = useState(true);
  const [fieldSegmentsCollapsed, setFieldSegmentsCollapsed] = useState(false);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(true);

  // Dropdown/accordion open states
  const [controlsOpen, setControlsOpen] = useState(false);
  const [mapModeOpen, setMapModeOpen] = useState(false);
  const [simplificationOpen, setSimplificationOpen] = useState(false);
  const [polygonToggleOpen, setPolygonToggleOpen] = useState(false);
  const [obstructionsOpen, setObstructionsOpen] = useState(false);
  const [snapEdgeOpen, setSnapEdgeOpen] = useState(false);
  const [moduleAdvancedOpen, setModuleAdvancedOpen] = useState(false);

  // Auto-collapse/expand menu based on compactMode
  useEffect(() => {
    if (compactMode) {
      setMenuCollapsed(true);
    } else {
      setMenuCollapsed(false);
    }
  }, [compactMode]);

  // Convenience toggle methods
  const toggleRoofCard = useCallback(() => setRoofCardCollapsed(prev => !prev), []);
  const toggleObstructionCard = useCallback(() => setObstructionCardCollapsed(prev => !prev), []);
  const toggleModuleCard = useCallback(() => setModuleCardCollapsed(prev => !prev), []);
  const toggleFieldSegments = useCallback(() => setFieldSegmentsCollapsed(prev => !prev), []);
  const toggleMenu = useCallback(() => setMenuCollapsed(prev => !prev), []);
  const toggleLeftPanel = useCallback(() => setLeftPanelCollapsed(prev => !prev), []);

  const closeAllDropdowns = useCallback(() => {
    setControlsOpen(false);
    setMapModeOpen(false);
    setSimplificationOpen(false);
    setPolygonToggleOpen(false);
    setObstructionsOpen(false);
    setSnapEdgeOpen(false);
    setModuleAdvancedOpen(false);
  }, []);

  return {
    // State
    roofCardCollapsed,
    obstructionCardCollapsed,
    moduleCardCollapsed,
    fieldSegmentsCollapsed,
    menuCollapsed,
    leftPanelCollapsed,
    controlsOpen,
    mapModeOpen,
    simplificationOpen,
    polygonToggleOpen,
    obstructionsOpen,
    snapEdgeOpen,
    moduleAdvancedOpen,

    // Setters
    setRoofCardCollapsed,
    setObstructionCardCollapsed,
    setModuleCardCollapsed,
    setFieldSegmentsCollapsed,
    setMenuCollapsed,
    setLeftPanelCollapsed,
    setControlsOpen,
    setMapModeOpen,
    setSimplificationOpen,
    setPolygonToggleOpen,
    setObstructionsOpen,
    setSnapEdgeOpen,
    setModuleAdvancedOpen,

    // Convenience methods
    toggleRoofCard,
    toggleObstructionCard,
    toggleModuleCard,
    toggleFieldSegments,
    toggleMenu,
    toggleLeftPanel,
    closeAllDropdowns,
  };
}

export default useUIState;
